import { z } from "zod";
import { RaritySchema, RarityOrdinalSchema, DropChanceSchema, RarityDistributionSchema, } from "./rarity.js";
import { StatsSchema } from "./stats.js";
import { EpochMillisSchema } from "./common.js";
/**
 * Wiki DTOs (WEBAPI_SPEC §5, W1-W12).
 * Chaque schéma correspond au dump JSON runtime d'un registre. Réponses cacheables (ETag).
 */
/** Rareté embarquée dans les dumps wiki : {ordinal, label}. */
const WikiRarityRefSchema = z.object({
    ordinal: RarityOrdinalSchema,
    label: RaritySchema,
});
/** W1 — Items & gear (ItemRegistry + GearCatalog). */
export const WikiItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    rarity: WikiRarityRefSchema,
    tier: z.number().int().nullable(),
    level: z.number().int().nullable(),
    material: z.string(),
    modelId: z.string().nullable(),
    stats: StatsSchema,
    set: z.string().nullable(),
    description: z.array(z.string()),
    /** Provenance générale, descriptive (ex « Butin de donjon », « Craft », « Boutique »). ALLOWLIST : jamais d'ids admin/creative. */
    source: z.string().nullable().optional(),
    /** % de roll (0-100) quand l'item est obtenu par tirage de loot ; null/absent si non applicable. */
    rollChancePct: z.number().min(0).max(100).nullable().optional(),
});
/** W1 (gear skin) — étend WikiItem avec class + minLevel. */
export const WikiGearSchema = WikiItemSchema.extend({
    class: z.string().nullable(),
    minLevel: z.number().int().nullable(),
});
/** W2 — Enchants (EnchantService.exportAsJson). */
export const WikiEnchantSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.array(z.string()),
    rarity: WikiRarityRefSchema,
    tier: z.number().int(),
    target: z.string(),
    hook: z.string(),
    power: z.number(),
    cooldown: z.number().nullable(),
    cost: z.object({
        money: z.number().nullable(),
        dust: z.number().nullable(),
        book: z.number().nullable(),
    }),
    conditions: z.object({
        class: z.string().nullable(),
        level: z.number().int().nullable(),
    }),
    effects: z.array(z.string()),
    variants: z.array(z.string()),
});
/** W3 — Job XP curve sérialisée + seuils précalculés. */
export const WikiLevelCurveSchema = z.object({
    base: z.number(),
    exponent: z.number(),
    maxLevel: z.number().int(),
    /** thresholds[maxLevel] : XP cumulée pour atteindre chaque niveau. */
    thresholds: z.array(z.number()),
});
/**
 * Probabilité d'obtention d'une entrée de collection (variante de métier).
 * `percent` = % 0-100 ; `oneIn` = « 1 sur N » (entier) ; `label` = libellé FR pré-formaté
 * (ex « 1 sur 250 (0,4 %) »). Tout optionnel : les dumps amont peuvent n'en fournir qu'une partie.
 */
export const WikiProbabilitySchema = z.object({
    percent: z.number().min(0).max(100).nullable().optional(),
    oneIn: z.number().nullable().optional(),
    label: z.string().nullable().optional(),
});
/**
 * W3 — Collection d'un métier (~508/métier).
 * NOUVEAUX champs (dumps enrichis) : `skin`/`modelId` (rendu visuel de la variante) + `probability`
 * (chance d'obtention). Tous OPTIONNELS et jamais retirés — passthrough intégral côté BFF.
 */
export const WikiCollectionSchema = z.object({
    id: z.string(),
    name: z.string(),
    material: z.string().nullable(),
    rarity: WikiRarityRefSchema.nullable(),
    tiers: z.array(z.object({ amount: z.number().int(), reward: z.string().nullable() })).optional(),
    /** Skin/texture de la variante (id de skin custom). */
    skin: z.string().nullable().optional(),
    /** Modèle custom (CustomModelData ou id de modèle). */
    modelId: z.string().nullable().optional(),
    /** Probabilité d'obtention de cette variante ({percent, oneIn, label}). */
    probability: WikiProbabilitySchema.nullable().optional(),
});
/** W3 — Métier complet. */
export const WikiJobSchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.array(z.string()),
    icon: z.string().nullable(),
    curve: WikiLevelCurveSchema,
    actions: z.array(z.string()),
    perks: z.array(z.object({ level: z.number().int(), name: z.string(), description: z.string() })),
    collections: z.array(WikiCollectionSchema),
});
/** W5 — Mob / boss. */
export const WikiMobDropSchema = z.object({
    item: z.string(),
    custom: z.boolean(),
    /** chance brute historique (0-1) — conservée pour compat ; préférer dropChancePct pour l'affichage. */
    chance: z.number(),
    /** % de drop (0-100), champ loot canonique exposé au wiki. */
    dropChancePct: z.number().min(0).max(100),
    min: z.number().int(),
    max: z.number().int(),
});
export const WikiMobSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    tier: z.number().int().nullable(),
    theme: z.string().nullable(),
    health: z.number(),
    damage: z.number(),
    armor: z.number(),
    speed: z.number(),
    xp: z.number(),
    boss: z.boolean(),
    barColor: z.string().nullable(),
    glowing: z.boolean(),
    drops: z.array(WikiMobDropSchema),
});
/** W6 — Zone. */
export const WikiZoneSchema = z.object({
    id: z.string(),
    name: z.string(),
    tier: z.number().int().nullable(),
    theme: z.string().nullable(),
    center: z.object({ x: z.number(), z: z.number() }),
    seed: z.number().nullable(),
    state: z.string(),
    questIds: z.array(z.string()),
    clearedQuestIds: z.array(z.string()),
    tensionPhrase: z.string().nullable(),
    radius: z.number().nullable(),
});
/** W7 — Boss de donjon. */
export const WikiBossSchema = z.object({
    id: z.string(),
    name: z.string(),
    mobId: z.string().nullable(),
    health: z.number().nullable(),
    phases: z.number().int().nullable(),
});
/** W7 — Vague/phase de donjon. */
export const WikiDungeonWaveSchema = z.object({
    index: z.number().int(),
    mobs: z.array(z.object({ mobId: z.string(), count: z.number().int() })),
    boss: z.boolean().optional(),
});
/** W7 — Template de donjon. */
export const WikiDungeonSchema = z.object({
    id: z.string(),
    name: z.string(),
    difficulty: z.string().nullable(),
    levelBand: z.object({ min: z.number().int(), max: z.number().int() }).nullable(),
    waves: z.array(WikiDungeonWaveSchema),
    boss: WikiBossSchema.nullable(),
    rewards: z.array(z.object({
        item: z.string(),
        /** chance brute (0-1), compat historique. */
        chance: z.number(),
        /** % de drop (0-100), champ loot canonique. */
        dropChancePct: z.number().min(0).max(100),
    })),
});
/**
 * W8 — Craft (~2572 variantes).
 * NOUVEAUX champs (dumps enrichis) : `tier` (palier du craft) + `element` (affinité élémentaire,
 * ex « Feu », « Foudre »). Tous deux OPTIONNELS et jamais retirés — passthrough intégral côté BFF.
 */
export const WikiCraftSchema = z.object({
    name: z.string(),
    type: z.enum(["shaped", "shapeless"]),
    shape: z.array(z.string()),
    keys: z.record(z.string(), z.string()),
    ingredients: z.array(z.string()),
    result: z.object({
        id: z.string().nullable(),
        material: z.string().nullable(),
        custom: z.boolean(),
        amount: z.number().int(),
    }),
    category: z.string().nullable(),
    /**
     * Palier du craft (progression) ; null/absent si non applicable. Les dumps enrichis servent ce champ
     * SOIT en nombre (palier numérique), SOIT en CHAÎNE (libellé de palier, ex « Maître », « III »). On
     * accepte les deux (union) — le BFF coerce les chaînes purement numériques vers un nombre, mais laisse
     * passer les libellés non numériques sans les perdre.
     */
    tier: z.union([z.number().int(), z.string()]).nullable().optional(),
    /** Affinité élémentaire du résultat (ex « Feu ») ; null/absent si neutre. */
    element: z.string().nullable().optional(),
});
/** W9 — Recette de cuisine. */
export const WikiCookingRecipeSchema = z.object({
    id: z.string(),
    display: z.string(),
    icon: z.string().nullable(),
    cookSeconds: z.number(),
    unlockLevel: z.object({ job: z.number().int().nullable(), net: z.number().int().nullable() }),
    moneyCost: z.number().nullable(),
    stationTier: z.number().int().nullable(),
    ingredients: z.array(z.string()),
    result: z.string(),
    buffs: z.array(z.string()),
});
/**
 * Déclinaison d'un nœud de récolte (par minerai/grade/qualité).
 * NOUVEAU (dumps enrichis) : chaque nœud liste ses variantes de résultat avec leur probabilité.
 * `resultItem`/`id` = id de l'item produit (l'un ou l'autre selon la source amont, les deux tolérés).
 */
export const WikiHarvestDeclensionSchema = z.object({
    grade: z.string().nullable().optional(),
    label: z.string().nullable().optional(),
    probabilityPct: z.number().min(0).max(100).nullable().optional(),
    resultItem: z.string().nullable().optional(),
    id: z.string().nullable().optional(),
    resultName: z.string().nullable().optional(),
});
/** W10 — Nœud de récolte / culture (clés FR, cf. exporter). */
export const WikiHarvestNodeSchema = z.object({
    id: z.string(),
    nom: z.string(),
    famille: z.string().nullable(),
    metier: z.string().nullable(),
    niveauRequis: z.number().int().nullable(),
    vivace: z.boolean(),
    soifMinutes: z.number().nullable(),
    etapes: z.array(z.string()),
    recolte: z.string().nullable(),
    conditions: z.record(z.string(), z.unknown()),
    produit: z.string().nullable(),
    effet: z.string().nullable(),
    /**
     * Chance de récolte : chancePct global + perGrade = poids par déclinaison/grade
     * (ex tier de nœud, qualité de la culture) → % de produit rare. ALLOWLIST descriptive.
     */
    chance: DropChanceSchema.optional(),
    /**
     * NOUVEAU (dumps enrichis) : déclinaisons par minerai/grade — chaque variante de résultat avec sa
     * probabilité. OPTIONNEL et jamais retiré (passthrough BFF).
     */
    declensions: z.array(WikiHarvestDeclensionSchema).optional(),
});
/** W11 — Rareté (tables statiques). */
export const WikiRaritySchema = z.object({
    rarities: z.array(z.object({
        ordinal: RarityOrdinalSchema,
        label: RaritySchema,
        statMultiplier: z.number(),
        color: z.string(),
    })),
    /** Distribution globale des raretés (rareté → %) pour les tables de loot génériques. */
    rarityDistribution: RarityDistributionSchema,
});
/**
 * W (NOUVEAU) — Grade/rang réel exposé par /api/wiki/ranks (Etheria_Ranks).
 *  - id/name  : identifiant technique + nom affiché (FR).
 *  - weight   : poids hiérarchique (grand = plus élevé) — sert au tri et au re-contrôle.
 *  - prefix   : préfixe de chat/tab (peut contenir des codes couleur).
 *  - permissions : liste des permissions octroyées par le grade.
 *  - staff    : true si c'est un grade de staff (modération/admin).
 *  - price    : coût d'achat en jeu (null si non achetable). Nombre ou chaîne (BigDecimal), nullable.
 */
export const WikiRankSchema = z.object({
    id: z.string(),
    name: z.string(),
    weight: z.number().int(),
    prefix: z.string().nullable(),
    permissions: z.array(z.string()),
    staff: z.boolean(),
    price: z.union([z.number(), z.string()]).nullable(),
});
/** Enveloppe liste des grades : { ranks:[...] } (forme servie par le BFF). */
export const WikiRanksPayloadSchema = z.object({ ranks: z.array(WikiRankSchema) });
// ── Classements de donjons (WEBAPI_SPEC §5 — LeaderboardService/ClearsStore) ──
/** Une entrée de classement de donjon (clearMs pour un run chronométré, sinon score). */
export const DungeonLeaderboardEntrySchema = z.object({
    rank: z.number().int().positive(),
    player: z.string(),
    /** temps de clear en millisecondes (mode chrono) ; null si classement au score. */
    clearMs: z.number().int().nonnegative().nullable(),
    /** score (mode points) ; null si classement au temps. */
    score: z.number().nullable(),
    clearedAt: EpochMillisSchema,
});
/** Classement d'un donjon pour une difficulté donnée (Etheria_Dungeons LeaderboardService). */
export const DungeonLeaderboardSchema = z.object({
    dungeonId: z.string(),
    difficulty: z.string(),
    entries: z.array(DungeonLeaderboardEntrySchema),
});
/** Requête de classement de donjon : ?dungeonId=&difficulty=&limit=&offset=. */
export const DungeonLeaderboardQuerySchema = z.object({
    dungeonId: z.string(),
    difficulty: z.string().optional(),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
});
// ── Enveloppes de payload (forme consommée par le frontend) ──────────────────
//
// Le dump amont d'Etheria_WebAPI est un TABLEAU BRUT par catégorie (items/enchants/…), OU un objet
// pour rarity/index. Le frontend (`apps/web/src/api/wiki.ts` + pages) attend en revanche des payloads
// ENVELOPPÉS ({ jobs:[...] }, { recipes:[...] }, et surtout items SPLIT en { items, gear }). Ces
// schémas décrivent la forme finale servie par le BFF au navigateur — le contrat effectif. Le BFF
// valide chaque élément avec les schémas ci-dessus PUIS ré-enveloppe dans ces formes. Aucun champ
// présent dans le dump n'est perdu : on ne fait que regrouper/scinder, pas filtrer.
/**
 * W1 — items : le dump amont est un tableau plat mêlant items curated et skins de gear (un skin porte
 * `class`/`minLevel`). Le frontend consomme deux listes distinctes → on scinde en { items, gear }.
 */
export const WikiItemsPayloadSchema = z.object({
    items: z.array(WikiItemSchema),
    gear: z.array(WikiGearSchema),
});
/** W2 — enchants : { enchants:[...] }. */
export const WikiEnchantsPayloadSchema = z.object({ enchants: z.array(WikiEnchantSchema) });
/** W3 — métiers : { jobs:[...] }. */
export const WikiJobsPayloadSchema = z.object({ jobs: z.array(WikiJobSchema) });
/** W5 — mobs : { mobs:[...] }. */
export const WikiMobsPayloadSchema = z.object({ mobs: z.array(WikiMobSchema) });
/** W6 — zones : { zones:[...] }. */
export const WikiZonesPayloadSchema = z.object({ zones: z.array(WikiZoneSchema) });
/** W7 — donjons : { dungeons:[...] }. */
export const WikiDungeonsPayloadSchema = z.object({ dungeons: z.array(WikiDungeonSchema) });
/** W8 — artisanat : { crafts:[...] }. */
export const WikiCraftsPayloadSchema = z.object({ crafts: z.array(WikiCraftSchema) });
/** W9 — cuisine : { recipes:[...] } (clé `recipes`, pas `cooking`). */
export const WikiCookingPayloadSchema = z.object({ recipes: z.array(WikiCookingRecipeSchema) });
/** W10 — récolte : { nodes:[...] } (clé `nodes`, pas `harvest`). */
export const WikiHarvestPayloadSchema = z.object({ nodes: z.array(WikiHarvestNodeSchema) });
// ── OBTENTION (index item → sources) — GET /api/wiki/obtention/:id ────────────
//
// Le GRAPHE D'OBTENTION répond à la question « comment obtenir cet objet ? » en joignant les dumps
// wiki déjà proxifiés : recettes d'artisanat (result.id/material → grille), butin de mob (drops → %),
// nœuds de récolte (produit/déclinaisons → %), collections de métier (variantes → probabilité) et,
// quand disponible, la boutique. Construit et caché côté BFF ; strictement lecture seule (FR).
/** Une source « artisanat » : recette qui produit l'objet (grille reconstituable côté client). */
export const ObtentionCraftSourceSchema = z.object({
    kind: z.literal("craft"),
    /** Nom de la recette (adressable /wiki/crafts/:name). */
    recipe: z.string(),
    type: z.enum(["shaped", "shapeless"]),
    shape: z.array(z.string()),
    keys: z.record(z.string(), z.string()),
    ingredients: z.array(z.string()),
    amount: z.number().int(),
    category: z.string().nullable(),
    tier: z.union([z.number().int(), z.string()]).nullable(),
    element: z.string().nullable(),
});
/** Une source « mob » : créature qui laisse tomber l'objet, avec % de butin. */
export const ObtentionMobSourceSchema = z.object({
    kind: z.literal("mob"),
    mobId: z.string(),
    mobName: z.string(),
    boss: z.boolean(),
    theme: z.string().nullable(),
    dropChancePct: z.number(),
    min: z.number().int(),
    max: z.number().int(),
});
/** Une source « donjon » : récompense d'un template de donjon, avec % de drop. */
export const ObtentionDungeonSourceSchema = z.object({
    kind: z.literal("dungeon"),
    dungeonId: z.string(),
    dungeonName: z.string(),
    difficulty: z.string().nullable(),
    dropChancePct: z.number(),
});
/** Une source « récolte » : nœud/culture qui produit l'objet (déclinaison + %). */
export const ObtentionHarvestSourceSchema = z.object({
    kind: z.literal("harvest"),
    nodeId: z.string(),
    nodeName: z.string(),
    famille: z.string().nullable(),
    metier: z.string().nullable(),
    /** Grade/déclinaison (ex « Rare », minerai) si l'objet vient d'une variante précise. */
    declension: z.string().nullable(),
    probabilityPct: z.number().nullable(),
});
/** Une source « métier » : entrée de collection d'un métier (variante + probabilité). */
export const ObtentionJobSourceSchema = z.object({
    kind: z.literal("job"),
    jobKey: z.string(),
    jobName: z.string(),
    collectionName: z.string(),
    probability: WikiProbabilitySchema.nullable(),
});
/** Une source « boutique » : article vendu/acheté (prix), si un catalogue est disponible. */
export const ObtentionShopSourceSchema = z.object({
    kind: z.literal("shop"),
    shopName: z.string().nullable(),
    buyPrice: z.number().nullable(),
    sellPrice: z.number().nullable(),
});
/** Union discriminée de toutes les sources d'obtention. */
export const ObtentionSourceSchema = z.discriminatedUnion("kind", [
    ObtentionCraftSourceSchema,
    ObtentionMobSourceSchema,
    ObtentionDungeonSourceSchema,
    ObtentionHarvestSourceSchema,
    ObtentionJobSourceSchema,
    ObtentionShopSourceSchema,
]);
/**
 * Réponse d'obtention pour un objet donné : son libellé résolu (nom FR si connu),
 * un renvoi wiki canonique (catégorie + route) quand l'objet a une fiche, et la liste
 * de toutes ses sources (craft/mob/donjon/récolte/métier/boutique).
 */
export const ObtentionResponseSchema = z.object({
    /** Clé demandée (id ou matière, telle que reçue). */
    key: z.string(),
    /** Nom d'affichage résolu (FR si connu, sinon la clé). */
    name: z.string(),
    /** Rareté résolue si l'objet est connu au registre items/collection (sinon null). */
    rarity: WikiRarityRefSchema.nullable(),
    /** Lien vers la fiche wiki canonique de l'objet ({cat, route}) s'il en a une. */
    wikiEntry: z.object({ cat: z.string(), route: z.string() }).nullable(),
    /** Toutes les provenances trouvées, ordonnées par pertinence côté BFF. */
    sources: z.array(ObtentionSourceSchema),
});
/**
 * Index d'obtention (léger) : liste des clés obtenables + leur nom résolu. Sert d'ANNUAIRE pour
 * savoir quelles mentions rendre cliquables côté frontend sans requêter chaque objet. Caché (ETag).
 */
export const ObtentionIndexSchema = z.object({
    /** Nombre total de clés indexées. */
    count: z.number().int().nonnegative(),
    /** Map clé → { name, sources: nombre de provenances }. */
    keys: z.record(z.string(), z.object({ name: z.string(), sources: z.number().int().nonnegative() })),
});
/** W12 — Manifest léger : {registry → {count, etag, updatedAt}}. */
export const WikiIndexEntrySchema = z.object({
    count: z.number().int().nonnegative(),
    etag: z.string(),
    updatedAt: EpochMillisSchema,
});
export const WikiIndexSchema = z.record(z.string(), WikiIndexEntrySchema);
/** Catégories wiki adressables (route /api/wiki/:category). */
export const WIKI_CATEGORIES = [
    "items",
    "enchants",
    "jobs",
    "mobs",
    "zones",
    "dungeons",
    "crafts",
    "cooking",
    "harvest",
    "rarity",
    "index",
];
export const WikiCategorySchema = z.enum(WIKI_CATEGORIES);
/** Libellés FR des catégories wiki. */
export const WIKI_CATEGORY_LABEL_FR = {
    items: "Items & Gear",
    enchants: "Enchantements",
    jobs: "Métiers",
    mobs: "Mobs & Boss",
    zones: "Zones",
    dungeons: "Donjons",
    crafts: "Artisanat",
    cooking: "Cuisine",
    harvest: "Récolte",
    rarity: "Raretés",
    index: "Index",
};
//# sourceMappingURL=wiki.js.map